import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../context/AuthContext'
import Loader from '../../components/Loader'
import { PRIVACY_STORAGE_KEY, DEFAULT_PRIVACY, getPrivacyPreferences } from '../../lib/privacyPreferences'
import '../onboarding/onboarding.css'
import './settings.css'

const DEFAULT_COOKIES = DEFAULT_PRIVACY

function loadStoredPrivacy() {
  return getPrivacyPreferences()
}

function saveStoredPrivacy(toggles) {
  try {
    localStorage.setItem(PRIVACY_STORAGE_KEY, JSON.stringify(toggles))
  } catch (e) {
    console.warn('[SettingsPrivacy] saveStoredPrivacy', e)
  }
}

const COOKIE_KEYS = [
  { key: 'essential', labelKey: 'privacy.essentialCookies', descKey: 'privacy.essentialCookiesDesc' },
  { key: 'functional', labelKey: 'privacy.functionalCookies', descKey: 'privacy.functionalCookiesDesc' },
  { key: 'performance', labelKey: 'privacy.performanceCookies', descKey: 'privacy.performanceCookiesDesc' },
  { key: 'advertising', labelKey: 'privacy.advertisingCookies', descKey: 'privacy.advertisingCookiesDesc' }
]

const NETLIFY_FUNCTIONS = import.meta.env.VITE_NETLIFY_FUNCTIONS_URL || ''

const SettingsPrivacy = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuth()
  const [toggles, setToggles] = useState(DEFAULT_COOKIES)
  const [email, setEmail] = useState(user?.email ?? '')
  const [password, setPassword] = useState('')
  const [requestSent, setRequestSent] = useState(false)
  const [requestError, setRequestError] = useState('')
  const [requesting, setRequesting] = useState(false)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    setToggles(loadStoredPrivacy())
  }, [])

  const onBack = () => navigate('/app/profile/settings')
  const toggle = (key) => {
    setToggles((t) => ({ ...t, [key]: !t[key] }))
    setDirty(true)
  }
  const onConfirm = () => {
    saveStoredPrivacy(toggles)
    setDirty(false)
  }
  const onAcceptAll = () => {
    const all = { essential: true, functional: true, performance: true, advertising: true }
    setToggles(all)
    saveStoredPrivacy(all)
    setDirty(false)
  }
  const onRequestData = async (e) => {
    e.preventDefault()
    if (!email.trim() || !password) return
    setRequestError('')
    setRequesting(true)
    try {
      const url = NETLIFY_FUNCTIONS ? `${NETLIFY_FUNCTIONS}/request-account-data` : '/.netlify/functions/request-account-data'
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setRequestError(data.error || t('privacy.requestFailed'))
        setRequesting(false)
        return
      }
      setRequestSent(true)
      setPassword('')
    } catch (err) {
      setRequestError(t('privacy.requestFailed'))
    }
    setRequesting(false)
  }

  return (
    <div className="onboarding-page">
      <div className="onboarding-content" style={{ paddingTop: 48, paddingBottom: 100 }}>
        <div style={{ marginBottom: 24 }}>
          <button type="button" className="link" onClick={onBack}>← {t('common.back')}</button>
        </div>
        <h1 className="h1">{t('privacy.title')}</h1>

        <h2 className="h1" style={{ marginTop: 24, marginBottom: 12 }}>{t('privacy.privacySettings')}</h2>
        {COOKIE_KEYS.map(({ key, labelKey, descKey }) => (
          <div key={key} className="settings-row">
            <div>
              <div style={{ fontSize: 15 }}>{t(labelKey)}</div>
              <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>{t(descKey)}</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={toggles[key]}
              className={`settings-toggle ${toggles[key] ? 'on' : ''}`}
              onClick={() => toggle(key)}
            >
              <span className="settings-toggle-knob" />
            </button>
          </div>
        ))}
        {dirty && (
          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button type="button" className="settings-modal-btn settings-modal-btn-primary" onClick={onConfirm}>
              {t('common.save')}
            </button>
            <button type="button" className="settings-modal-btn settings-modal-btn-secondary" onClick={onAcceptAll}>
              {t('onboarding.cookiesAcceptAll')}
            </button>
          </div>
        )}

        <h2 className="h1" style={{ marginTop: 32, marginBottom: 12 }}>{t('privacy.requestAccountData')}</h2>
        <p className="p" style={{ marginBottom: 16 }}>
          {t('privacy.requestAccountData')}
        </p>
        {requestSent ? (
          <p className="p" style={{ color: 'var(--gray)' }}>
            We'll provide your account data within 30 days.
          </p>
        ) : (
          <form onSubmit={onRequestData}>
            <div className="field">
              <label className="label">{t('privacy.emailLabel')}</label>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
            <div className="field">
              <label className="label">{t('loginPassword.passwordLabel')}</label>
              <input
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            {requestError && <div className="error-text" style={{ marginTop: 8 }}>{requestError}</div>}
            <button
              type="submit"
              className="settings-modal-btn settings-modal-btn-primary"
              style={{ width: '100%', marginTop: 16 }}
              disabled={requesting}
            >
              {requesting ? <Loader text={t('common.sending')} inline /> : t('privacy.requestData')}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

export default SettingsPrivacy
